<template>
    <div
        v-if="isVisible"
        class="fixed inset-0 z-50 flex items-center justify-center bg-black/65"
        @click.self="close"
    >
        <div class="bg-bg border-stroke-1 mx-4 w-full max-w-md border shadow-xl">
            <div class="p-4">
                <div v-if="mode === 'player-to-team'" class="mb-6 text-center">
                    <h2 class="font-title mb-2 text-2xl font-bold text-white uppercase">
                        {{ $t('team_assignment.title') }}
                    </h2>
                    <i18n-t
                        keypath="team_assignment.choose_team"
                        tag="p"
                        class="flex flex-col items-center justify-center text-sm text-gray-400"
                    >
                        <template #name>
                            <strong class="line-clamp-1 max-w-40 truncate text-white">{{
                                player?.tag_name
                            }}</strong>
                        </template>
                    </i18n-t>
                </div>

                <div v-if="mode === 'team-to-player'" class="mb-6 text-center">
                    <h2 class="font-title mb-2 text-2xl font-bold text-white uppercase">
                        {{ $t('team_assignment.player_selection') }}
                    </h2>
                    <i18n-t
                        keypath="team_assignment.choose_player"
                        tag="p"
                        class="flex flex-col items-center justify-center text-sm text-gray-400"
                    >
                        <template #team>
                            <strong class="text-white">{{ $t('team_assignment.team_' + selectedTeamNumber) }}</strong>
                        </template>
                    </i18n-t>
                </div>

                <div
                    v-if="mode === 'player-to-team'"
                    class="mb-6 flex items-center justify-center space-x-4"
                >
                    <div
                        :class="[
                            'border-stroke-1 flex h-16 w-16 items-center justify-center border text-sm font-bold text-white',
                            player?.ready ? 'bg-green-600' : 'bg-bg',
                        ]"
                    >
                        {{ player?.tag_name?.charAt(0).toUpperCase() }}
                    </div>
                    <div>
                        <h3 class="text-lg font-bold text-white">
                            <span class="line-clamp-1 max-w-40 truncate">{{
                                player?.tag_name
                            }}</span>
                        </h3>
                        <p class="text-sm text-gray-400">
                            {{ player?.ready ? $t('team_assignment.ready') : $t('team_assignment.pending') }}
                        </p>
                    </div>
                </div>

                <div v-if="mode === 'player-to-team'" class="space-y-3">
                    <button
                        @click="selectTeam(1)"
                        :class="[
                            'w-full border p-4 transition-colors',
                            selectedTeam === 1
                                ? 'border-red-500 bg-red-600'
                                : 'bg-bg border-stroke-1 hover:border-gray-300',
                        ]"
                    >
                        <div class="flex items-center justify-between">
                            <div class="flex items-center space-x-3">
                                <div class="text-lg font-bold text-white">{{ $t('team_assignment.team_1') }}</div>
                                <div class="text-sm text-gray-400">
                                    {{ team1Count }} {{ team1Count === 1 ? $t('team_assignment.player_singular') : $t('team_assignment.player_plural') }}
                                </div>
                            </div>
                            <div v-if="selectedTeam === 1" class="text-white">✓</div>
                        </div>
                    </button>

                    <button
                        @click="selectTeam(2)"
                        :class="[
                            'w-full border p-4 transition-colors',
                            selectedTeam === 2
                                ? 'border-red-500 bg-red-600'
                                : 'bg-bg border-stroke-1 hover:border-gray-300',
                        ]"
                    >
                        <div class="flex items-center justify-between">
                            <div class="flex items-center space-x-3">
                                <div class="text-lg font-bold text-white">{{ $t('team_assignment.team_2') }}</div>
                                <div class="text-sm text-gray-400">
                                    {{ team2Count }} {{ team2Count === 1 ? $t('team_assignment.player_singular') : $t('team_assignment.player_plural') }}
                                </div>
                            </div>
                            <div v-if="selectedTeam === 2" class="text-white">✓</div>
                        </div>
                    </button>
                </div>

                <div v-if="mode === 'team-to-player'" class="max-h-80 space-y-3 overflow-y-auto">
                    <button
                        v-for="availablePlayer in availablePlayers"
                        :key="availablePlayer.id"
                        @click="selectPlayer(availablePlayer)"
                        :class="[
                            'w-full border p-4 transition-colors',
                            selectedPlayerId === availablePlayer.id
                                ? 'border-red-500 bg-red-600'
                                : 'bg-bg border-stroke-1 hover:border-gray-300',
                        ]"
                    >
                        <div class="flex items-center justify-between">
                            <div class="flex items-center space-x-3">
                                <div
                                    :class="[
                                        'border-stroke-1 flex h-10 w-10 items-center justify-center border text-xs font-bold text-white',
                                        availablePlayer.ready ? 'bg-green-600' : 'bg-bg',
                                    ]"
                                >
                                    {{ availablePlayer.tag_name.charAt(0).toUpperCase() }}
                                </div>
                                <div class="text-left">
                                    <div class="text-sm font-bold text-white">
                                        {{ availablePlayer.tag_name }}
                                    </div>
                                    <div class="text-xs text-gray-400">
                                        {{ availablePlayer.ready ? $t('team_assignment.ready') : $t('team_assignment.pending') }}
                                    </div>
                                </div>
                            </div>
                            <div v-if="selectedPlayerId === availablePlayer.id" class="text-white">
                                ✓
                            </div>
                        </div>
                    </button>

                    <div
                        v-if="availablePlayers.length === 0"
                        class="py-4 text-center text-gray-400"
                    >
                        {{ $t('team_assignment.no_unassigned') }}
                    </div>
                </div>
            </div>

            <div class="border-stroke-1 mt-4 grid grid-cols-2 gap-4 border-t p-4">
                <DiagonalCutButton
                    variant="secondary"
                    cut-position="right-top"
                    @click="close"
                    :disabled="isAssigning"
                >
                    {{ $t('general.cancel') }}
                </DiagonalCutButton>
                <DiagonalCutButton
                    variant="primary"
                    cut-position="left-top"
                    @click="assign"
                    :disabled="!canAssign || isAssigning"
                >
                    <span v-if="isAssigning">{{ $t('team_assignment.assigning') }}</span>
                    <span v-else>{{ $t('team_assignment.assign') }}</span>
                </DiagonalCutButton>
            </div>
        </div>
    </div>
</template>

<script>
import DiagonalCutButton from '@/components/ui/DiagonalCutButton.vue'

export default {
    name: 'TeamAssignmentModal',
    components: { DiagonalCutButton },
    props: {
        isVisible: {
            type: Boolean,
            default: false,
        },
        mode: {
            type: String,
            default: 'player-to-team',
        },
        player: {
            type: Object,
            default: null,
        },
        selectedTeamNumber: {
            type: Number,
            default: null,
        },
        availablePlayers: {
            type: Array,
            default: () => [],
        },
        team1Count: {
            type: Number,
            default: 0,
        },
        team2Count: {
            type: Number,
            default: 0,
        },
        isAssigning: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['assign-team', 'assign-player', 'close'],
    data() {
        return {
            selectedTeam: null,
            selectedPlayerId: null,
        }
    },
    computed: {
        canAssign() {
            if (this.mode === 'player-to-team') {
                return this.selectedTeam && this.player
            } else if (this.mode === 'team-to-player') {
                return this.selectedPlayerId && this.selectedTeamNumber
            }
            return false
        },
    },
    watch: {
        isVisible(newValue) {
            if (newValue) {
                this.selectedTeam = null
                this.selectedPlayerId = null
            }
        },
    },
    methods: {
        selectTeam(teamNumber) {
            this.selectedTeam = teamNumber
        },
        selectPlayer(player) {
            this.selectedPlayerId = player.id
        },
        assign() {
            if (this.mode === 'player-to-team') {
                this.assignPlayerToTeam()
            } else if (this.mode === 'team-to-player') {
                this.assignPlayerToTeam()
            }
        },
        assignPlayerToTeam() {
            if (this.mode === 'player-to-team' && this.selectedTeam && this.player) {
                this.$emit('assign-team', {
                    player: this.player,
                    teamNumber: this.selectedTeam,
                })
            } else if (
                this.mode === 'team-to-player' &&
                this.selectedPlayerId &&
                this.selectedTeamNumber
            ) {
                const selectedPlayer = this.availablePlayers.find(
                    (p) => p.id === this.selectedPlayerId,
                )
                if (selectedPlayer) {
                    this.$emit('assign-player', {
                        player: selectedPlayer,
                        teamNumber: this.selectedTeamNumber,
                    })
                }
            }
        },
        close() {
            this.selectedTeam = null
            this.selectedPlayerId = null
            this.$emit('close')
        },
    },
}
</script>
